import { GetReportsBetweenDatesResult, ReportType } from "../../../types/Rows";
import { PlannedReport } from "../../../api/reportsApi";
import { PLANNED_VISIT_STYLE, getPlannedVisitPeople, plannedVisitStatus } from "../../../utils/plannedVisitUtils";
import {
  parseDate,
  formatDateForCalendar,
  getStateColor,
  getStateName,
  escapeHtml
} from "../utils/calendarHelpers";

const CM_BACK_COLOR = "#7C3AED";
const CM_BORDER_COLOR = "#5B21B6";

export interface CalendarEvent {
  id: string;
  text: string;
  html?: string;
  start: string;
  end: string;
  backColor: string;
  borderColor: string;
  fontColor: string;
  toolTip: string;
  isPlanned?: boolean;
}

const transformRegularReportsToEvents = (
  reports: GetReportsBetweenDatesResult[],
  t: any
): CalendarEvent[] => {
  return reports
    .filter((report) => report.visitDate)
    .map((report) => {
      const startDate = parseDate(report.visitDate!);
      const endDate = report.closedDate 
        ? parseDate(report.closedDate)
        : startDate;
      
      // For multi-day events, add 1 day to end date for proper display
      const displayEndDate = new Date(endDate);
      displayEndDate.setDate(displayEndDate.getDate() + 1);

      const duration = report.closedDate 
        ? Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
        : 1;

      const isCM = report.reportType === ReportType.CENTRAL_MONITORING;
      const backColor = isCM ? CM_BACK_COLOR : getStateColor(report.state);
      const borderColor = isCM ? CM_BORDER_COLOR : "darker";
      const cmLabel = isCM ? "[CM] " : "";
      const reportName = escapeHtml(report.reportName);
      const studyName = escapeHtml(report.studyName);
      const resortName = escapeHtml(report.resortName);
      const createdBy = escapeHtml(report.createdBy);
      const createdByShort = escapeHtml(report.createdBy.split('@')[0]);

      return {
        id: report.id,
        text: `${cmLabel}${report.reportName}\n${report.resortName}\n${report.studyName}\n${report.createdBy.split('@')[0]}`,
        html: `
          <div style="font-size: 11px; line-height: 1.2; padding: 2px 4px; overflow: hidden; height: 100%;">
            <div style="font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${cmLabel}${reportName}</div>
            <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; opacity: 0.95;">${studyName}</div>
            <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; opacity: 0.9; font-size: 10px;">${resortName}</div>
            <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; opacity: 0.8; font-size: 9px;">${createdByShort}</div>
          </div>
        `,
        start: formatDateForCalendar(startDate),
        end: formatDateForCalendar(displayEndDate),
        backColor,
        borderColor,
        fontColor: "#ffffff",
        toolTip: `<div style="padding: 8px;"><strong>${cmLabel}${reportName}</strong><br/>Typ: ${isCM ? "Monitoring centralny" : "Standardowy"}<br/>Ośrodek: ${resortName}<br/>Badanie: ${studyName}<br/>Status: ${escapeHtml(getStateName(report.state, t))}<br/>Autor: ${createdBy}<br/>Data wizyty: ${startDate.toLocaleDateString('pl-PL')}${report.closedDate ? `<br/>Do: ${endDate.toLocaleDateString('pl-PL')}<br/>Czas: ${duration} dzień/dni` : ""}</div>`,
        isPlanned: false,
      };
    });
};

const transformPlannedReportsToEvents = (
  plannedReports: PlannedReport[],
  t: any
): CalendarEvent[] => {
  return plannedReports.map((planned) => {
    const startDate = new Date(planned.dateStart);
    const endDate = planned.dateEnd ? new Date(planned.dateEnd) : startDate;
    
    // For multi-day events, add 1 day to end date for proper display
    const displayEndDate = new Date(endDate);
    displayEndDate.setDate(displayEndDate.getDate() + 1);

    const duration = planned.dateEnd 
      ? Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
      : 1;

    const displayName = planned.name || 'Wizyta';
    const displayStudy = planned.studyName || '';
    const displayResort = planned.resortName || '';
    const createdBy = planned.createdBy?.split('@')[0] || '—';
    const participants = getPlannedVisitPeople(planned).filter((p) => p !== planned.createdBy);
    // Kolor wg stanu: przyszła (szara) / odbyta z raportem (zielona) / odbyta bez raportu (czerwona)
    const style = PLANNED_VISIT_STYLE[plannedVisitStatus(planned)];
    return {
      id: planned.id,
      text: `${displayName}\n${displayResort}\n${displayStudy}\n${createdBy}`,
      html: `
        <div style="font-size: 11px; line-height: 1.2; padding: 2px 4px; overflow: hidden; height: 100%; border-left: 3px solid ${style.border};">
          <div style="font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: ${style.font};">[${style.tag}] ${escapeHtml(displayName)}</div>
          <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; opacity: 0.95;">${escapeHtml(displayStudy)}</div>
          <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; opacity: 0.9; font-size: 10px;">${escapeHtml(displayResort)}</div>
          <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; opacity: 0.8; font-size: 9px;">${escapeHtml(createdBy)}</div>
        </div>
      `,
      start: formatDateForCalendar(startDate),
      end: formatDateForCalendar(displayEndDate),
      backColor: style.back,
      borderColor: style.border,
      fontColor: style.font,
      toolTip: `<div style="padding: 8px;"><strong>[${style.tag}] ${escapeHtml(displayName)}</strong><br/>Typ: Wizyta planowana<br/>Stan: ${style.label}<br/>Ośrodek: ${escapeHtml(displayResort)}<br/>Badanie: ${escapeHtml(displayStudy)}<br/>Planujący: ${escapeHtml(planned.createdBy)}<br/>Data wizyty: ${startDate.toLocaleDateString('pl-PL')}${planned.dateEnd && duration > 1 ? `<br/>Do: ${endDate.toLocaleDateString('pl-PL')}<br/>Czas: ${duration} dzień/dni` : ""}${participants.length ? `<br/>Uczestnicy: ${escapeHtml(participants.join(', '))}` : ""}</div>`,
      isPlanned: true,
    };
  });
};

export const transformReportsToEvents = (
  reports: GetReportsBetweenDatesResult[],
  plannedReports: PlannedReport[],
  t: any
): CalendarEvent[] => {
  const reportEvents = transformRegularReportsToEvents(reports, t);
  const plannedEvents = transformPlannedReportsToEvents(plannedReports, t);
  
  return [...reportEvents, ...plannedEvents];
};
